import React, {
  useCallback,
  useState,
  MouseEvent,
  ChangeEvent,
  useEffect,
} from 'react';
import {
  Modal,
  Fade,
  IconButton,
  Backdrop,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  makeStyles,
  createStyles,
} from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import { FiCamera } from 'react-icons/fi';
import { FaRegEdit } from 'react-icons/fa';
import { BsThreeDotsVertical } from 'react-icons/bs';
import {
  AmountDescription,
  ItemWrapper,
  MenuOptionButton,
  ModalImgWrapper,
  NewImg,
  PriceWrapper,
  StatusSaleButton,
} from '../styles';

import DeleteProduct from '../AcordeaoSimples/DeleteProduct';
import EditProdutoPizza from '../Drawers/Produtos/Edit/EditProdutoPizza';
import CloneProdutoPizza from '../Drawers/Produtos/Clone/ProdutoPizza';
import { useToast } from '../../../../hooks/toast';
import api from '../../../../services/api';

import no_image from '../../../../assets/no_image.png';
import BRLMoneyFormat from '../../../../utils/BRLMoneyFormat';
import { useRegisterAdded } from '../../../../hooks/registerAdded';

interface TableRowProdutoPizzaProps {
  product: any;
}

const ITEM_HEIGHT = 48;

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }),
);

const TableRowProdutoPizza: React.FC<TableRowProdutoPizzaProps> = ({
  product,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [vendaPausada, setVendaPausada] = useState(product.pausar_venda);
  const [produtoImg, setProdutoImg] = useState(product.imagem);

  const open = Boolean(anchorEl);

  const classes = useStyles();

  const { addToast } = useToast();
  const { handleSetHasProductUpdated } = useRegisterAdded();

  useEffect(() => {
    setProdutoImg(product.imagem);
  }, [product]);

  const handleModalOpen = (): void => {
    setModalOpen(true);
  };

  const handleModalClose = (): void => {
    setModalOpen(false);
  };

  const handleSetPaused = useCallback(
    (value: boolean) => {
      try {
        api
          .put(`/produto/${product.id}`, { pausar_venda: value })
          .then((response) => {
            const { pausar_venda } = response.data;

            addToast({
              type: 'info',
              title: `Produto ${product.descricao} ${
                pausar_venda ? 'desativado' : 'ativado'
              }.`,
            });

            setVendaPausada(pausar_venda);
          });
      } catch (err) {
        const { message } = err.response.data;

        addToast({
          type: 'error',
          title: message,
        });
      }
    },
    [addToast, product],
  );

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleImage = useCallback(() => {
    if (produtoImg) {
      if (typeof produtoImg === 'string') {
        return `${process.env.REACT_APP_SERVER_URL}/files/${produtoImg}`;
      }

      return URL.createObjectURL(produtoImg);
    }

    return no_image;
  }, [produtoImg]);

  const handleImageChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const data = new FormData();
        data.append('produto_img', e.target.files[0]);

        api.patch(`/produto/${product.id}`, data).then((response) => {
          setProdutoImg(response.data.imagem);

          handleSetHasProductUpdated(true);
          addToast({
            type: 'success',
            title: 'Imagem do produto atualizada',
          });
        });
      }
    },
    [product, addToast, handleSetHasProductUpdated],
  );

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <ItemWrapper img_url={handleImage()}>
          <button type="button" onClick={handleModalOpen}>
            <FiCamera size={17} />
          </button>
          <span>{product.descricao}</span>
        </ItemWrapper>
      </TableCell>

      <TableCell align="center">
        <AmountDescription>
          <button type="button">
            <BsThreeDotsVertical size={16} />
          </button>

          <div>
            <span>Disponível em</span>
            <p>{product.tamanho} tamanhos</p>
          </div>
        </AmountDescription>
      </TableCell>

      <TableCell align="center">
        <PriceWrapper>
          <div>
            <span>À partir de</span>
            <p>
              {Number(product.preco).toLocaleString('pt-BR', BRLMoneyFormat)}
            </p>
          </div>
        </PriceWrapper>
      </TableCell>

      <TableCell align="center">
        <StatusSaleButton
          type="button"
          isPaused={!vendaPausada}
          className="paused"
          onClick={() => handleSetPaused(true)}
        >
          Pausado
        </StatusSaleButton>

        <StatusSaleButton
          type="button"
          isPaused={vendaPausada}
          className="activated"
          onClick={() => handleSetPaused(false)}
        >
          Ativado
        </StatusSaleButton>
      </TableCell>

      <TableCell align="right">
        <IconButton
          aria-label="more"
          aria-controls="options-menu"
          aria-haspopup="true"
          onClick={handleClick}
        >
          <MoreVert />
        </IconButton>
        <Menu
          id="options-menu"
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              maxHeight: ITEM_HEIGHT * 4.5,
              width: '20ch',
            },
          }}
        >
          <MenuItem onClick={handleClose}>
            <EditProdutoPizza produto={product} />
          </MenuItem>
          <MenuItem onClick={handleClose}>
            <CloneProdutoPizza produto={product} />
          </MenuItem>
          <MenuItem onClick={handleClose}>
            <DeleteProduct
              descricao={product.descricao}
              produto_id={product.id}
              tipo_produto={3}
            />
          </MenuItem>
        </Menu>
      </TableCell>
      <Modal
        className={classes.modal}
        open={modalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalOpen}>
          <ModalImgWrapper>
            <div className="img_wrapper">
              <img
                alt={product.descricao}
                src={
                  produtoImg
                    ? `${process.env.REACT_APP_SERVER_URL}/files/${produtoImg}`
                    : `${no_image}`
                }
              />
            </div>

            <div className="divider" />

            <div className="action">
              <NewImg>
                <label htmlFor="change_img">
                  <FaRegEdit size={24} />
                  <input
                    type="file"
                    id="change_img"
                    onChange={handleImageChange}
                  />
                </label>
              </NewImg>
            </div>
          </ModalImgWrapper>
        </Fade>
      </Modal>
    </TableRow>
  );
};

export default TableRowProdutoPizza;
